import React from 'react';

import { useAppContext } from 'src/components/app-context';
import { usePriceRowContext } from '../../../price-row-context';
import { useOfferListFilterContext } from 'src/areas/product/components/offer-list-filter-context';

import { FontEmphasis, FontWeight } from '@prisjakt/ui/text/variants';
import { MobileAndDesktopOnly, TabletOnly } from 'src/ui/block';
import { OfferPriceLabel } from 'src/components/offer/price/offer-price';
import { PriceLabel } from 'src/components/offer/price/price-label';
import { AuthorizedDealerBadge } from '../authorized-dealer';
import { DeliveryTime } from '../delivery-time';
import { PriceTypeNoticeBadge } from './price-type-badge';
import { CountryBadge } from './country-badge';
import { StockBadge } from './stock-badge';

import { getCampaignByOfferId } from '../../../utils';

import { AlternativePrice } from 'src/types';
import { FilterType } from 'src/components/price-list/types';
import { OptionTypeEnum } from 'src/components/filter-dropdown';

import {
  Column,
  MediumUpFlex,
  FirstRow,
  MobileOnlyFlex,
  SecondRow,
  StyledCaptionText,
} from './styles';

interface IStockPriceInfo {
  alternativeOffer?: AlternativePrice;
}

export const StockPriceInfo: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IStockPriceInfo>
>> = props => {
  const { alternativeOffer } = props;
  const { t } = useAppContext();
  const { campaign, isFeatured, offer, sorting } = usePriceRowContext();
  const { getActiveFilters, getPriceDisplay } = useOfferListFilterContext();

  const { isInternationalPrice, store } = offer;

  // Get props from alternative offer if supplied
  const id = alternativeOffer ? alternativeOffer.id : offer.priceId;
  const selectedOffer = alternativeOffer || offer;
  const price = alternativeOffer ? alternativeOffer.price : offer.price;
  const offerPrices = alternativeOffer
    ? alternativeOffer.offerPrices
    : offer.offerPrices;
  const shipping = alternativeOffer
    ? alternativeOffer.shipping
    : offer.shipping;
  const stock = alternativeOffer ? alternativeOffer.stock : offer.stock;
  const priceType = alternativeOffer
    ? null // Not applicable for alternative offers
    : offer.priceType;

  const activeCampaign = getCampaignByOfferId(id, campaign);

  const isDownload = offer.priceType === 'download';

  const getDeliveryText = () => {
    const shippingInfo =
      sorting === OptionTypeEnum.FASTEST_DELIVERY
        ? shipping?.fastest
        : shipping?.cheapest;

    if (shippingInfo?.shippingCost === 0)
      return (
        <span>
          {shippingInfo?.carrier ? `${shippingInfo?.carrier}, ` : null}
          {t('product', 'price.free-shipping')}
        </span>
      );

    if (!shippingInfo?.shippingCost)
      return (
        <span>
          {shippingInfo?.carrier ? `${shippingInfo?.carrier}, ` : null}
          {t('product', 'price.unknown-shipping')}
        </span>
      );

    // Prefix the shipping cost with carrier or shipping label
    return shippingInfo?.carrier ? (
      <>
        {shippingInfo?.carrier},{' '}
        <PriceLabel
          value={shippingInfo.shippingCost}
          weight={FontWeight.NORMAL}
        />
      </>
    ) : (
      <>
        {t('product', 'delivery-time.label')}{' '}
        <PriceLabel
          value={shippingInfo.shippingCost}
          weight={FontWeight.NORMAL}
        />
      </>
    );
  };

  const deliveryText = getDeliveryText();

  const showDeliveryText = () => {
    if (!deliveryText) return false;

    const filteredOnShippingInfo = Boolean(
      getActiveFilters().find(
        item =>
          item.type === FilterType.PRICES_INC_SHIPPING ||
          item.type === FilterType.DELIVERY_METHOD ||
          item.type === FilterType.CARRIERS
      )
    );

    return (
      sorting === OptionTypeEnum.FASTEST_DELIVERY || filteredOnShippingInfo
    );
  };

  return (
    <Column>
      <FirstRow>
        {!isFeatured && (
          <MediumUpFlex>
            {isInternationalPrice && store?.primaryMarket && (
              <CountryBadge countryCode={store.primaryMarket} />
            )}
          </MediumUpFlex>
        )}
        {price && (
          <>
            {!isFeatured && (
              <StockBadge
                featuredOffer={isFeatured}
                status={stock?.status}
                statusText={stock?.statusText}
              />
            )}
            <OfferPriceLabel
              campaignColors={activeCampaign?.media?.colors}
              container="h4"
              priceDisplay={getPriceDisplay()}
              offer={selectedOffer}
              shipping={shipping ?? undefined}
              offerPrice={offerPrices}
            />
          </>
        )}
      </FirstRow>
      {isFeatured && (
        <>
          {showDeliveryText() ? (
            <StyledCaptionText emphasis={FontEmphasis.MEDIUM}>
              {deliveryText}
            </StyledCaptionText>
          ) : null}

          <SecondRow>
            {!alternativeOffer && offer.authorizedDealer && (
              <MobileAndDesktopOnly>
                <AuthorizedDealerBadge />
              </MobileAndDesktopOnly>
            )}
            <MediumUpFlex>
              {priceType && <PriceTypeNoticeBadge priceType={priceType} />}
              {isInternationalPrice && store?.primaryMarket && (
                <CountryBadge countryCode={store.primaryMarket} />
              )}
            </MediumUpFlex>
            <MediumUpFlex>
              {!isDownload && (
                <StockBadge
                  featuredOffer={isFeatured}
                  status={stock?.status}
                  statusText={stock?.statusText}
                />
              )}
            </MediumUpFlex>

            {!isDownload && (
              <MobileOnlyFlex>
                <StockBadge
                  featuredOffer={isFeatured}
                  status={stock?.status}
                  statusText={stock?.statusText}
                />
              </MobileOnlyFlex>
            )}
            {!isDownload && (
              <DeliveryTime shipping={shipping} stockStatus={stock?.status} />
            )}
          </SecondRow>

          {!alternativeOffer && offer.authorizedDealer && (
            <TabletOnly>
              <SecondRow>
                <AuthorizedDealerBadge />
              </SecondRow>
            </TabletOnly>
          )}
        </>
      )}
    </Column>
  );
};
